import { useEffect, useState } from 'react';
import type { CSSProperties, ReactNode } from 'react';

export type ProgressTone = 'brand' | 'success' | 'warning' | 'danger' | 'ai';

const fills: Record<ProgressTone, string> = {
    brand: 'var(--burgundy-700)',
    success: 'var(--success)',
    warning: 'var(--warning)',
    danger: 'var(--danger)',
    ai: 'var(--ai-gradient)',
};

export interface ProgressProps {
    value?: number;
    max?: number;
    tone?: ProgressTone;
    showValue?: boolean;
    size?: 'sm' | 'md' | 'lg';
    label?: ReactNode;
    animate?: boolean;
    style?: CSSProperties;
    /**
     * O nome que o leitor de tela anuncia.
     *
     * Obrigatório na prática quando não há `label` de texto: uma barra sem nome
     * é anunciada como "barra de progresso, 40%" — 40% de quê, o leitor não
     * sabe. Numa tabela de estoque com uma barra por linha, isso é uma lista de
     * porcentagens sem assunto.
     */
    ariaLabel?: string;
}

export function Progress({
    value = 0, max = 100, tone = 'brand', showValue = false, size = 'md', label, animate = true, style, ariaLabel,
}: ProgressProps) {
    const pct = Math.max(0, Math.min(100, (value / max) * 100));
    /* Nasce no valor final: se o efeito não rodar (documento oculto, prerender,
       impressão), a barra já está preenchida corretamente. A animação só recua
       para 0 quando há de fato uma janela visível para animar. */
    const [shown, setShown] = useState(pct);

    useEffect(() => {
        if (!animate || document.hidden) {
            setShown(pct);
            return;
        }
        const semMotion = typeof window.matchMedia === 'function'
            && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        if (semMotion) {
            setShown(pct);
            return;
        }
        setShown(0);
        const t = setTimeout(() => setShown(pct), 40);
        const garantia = setTimeout(() => setShown(pct), 900);
        return () => {
            clearTimeout(t);
            clearTimeout(garantia);
        };
    }, [pct, animate]);

    const fill = fills[tone] ?? fills.brand;
    const h = size === 'sm' ? 4 : size === 'lg' ? 10 : 6;

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, ...style }}>
            {(label || showValue) && (
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12.5, color: 'var(--text-secondary)' }}>
                    <span>{label}</span>
                    {showValue && (
                        <span style={{ fontWeight: 600, color: 'var(--text-primary)', fontVariantNumeric: 'tabular-nums' }}>
                            {Math.round(pct)}%
                        </span>
                    )}
                </div>
            )}
            <div
                role="progressbar" aria-valuenow={value} aria-valuemin={0} aria-valuemax={max}
                /* O texto do `label` serve de nome quando ele é texto; quando é
                   nó, só `ariaLabel` resolve. */
                aria-label={ariaLabel ?? (typeof label === 'string' ? label : undefined)}
                style={{ height: h, borderRadius: 'var(--radius-pill)', background: 'var(--neutral-100)', overflow: 'hidden' }}
            >
                <div style={{ width: `${shown}%`, height: '100%', borderRadius: 'inherit', background: fill, transition: 'width 700ms var(--ease-entrance)' }} />
            </div>
        </div>
    );
}
